/**
 * Story pane component for displaying and managing stories
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AgeProfile } from '../config/ageProfiles.config';
import { Story, StoryReadingState } from '../types/story.types';
// import { pronunciationService } from '../services/pronunciationService';
import { readingProgressService, ReadingProgressUpdate } from '../services/readingProgressService';
import { storyAnalysisService, StoryAnalysisResult } from '../services/storyAnalysisService';
import { summaryService } from '../services/summaryService';
import { logger } from '../utils/logger';
import { PronunciationWebSocketClient } from '../services/pronunciationWebSocketClient';
import PersonaIndicatorCompact from './PersonaIndicatorCompact';
// import PersonaReadinessBadge from './PersonaReadinessBadge';
import PronunciationProgressFloating from './PronunciationProgressFloating';
import SummaryGenerationModal from './SummaryGenerationModal';
import ReadingSummaryPanel from './ReadingSummaryPanel';
import { StoryProgressBar } from './StoryProgressBar';
import { getApiUrl } from '../utils/apiUtils';
import { ErrorCircleIcon, CheckCircleIcon, BookIcon } from './SvgConsolidated/SvgIcons';

interface StoryPaneProps {
  ageProfile: AgeProfile;
  userAge: number;
  userGrade?: string; // Add grade parameter for grade-based filtering
  conversationId: string;
  isRecording: boolean;
  onStorySelect: (story: Story | null) => void;
  onReadingModeChange: (readingState: StoryReadingState) => void;
  voiceLiveClient?: any; // Add voiceLiveClient as optional prop
  quizDialogEnabled?: boolean; // Quiz dialog enable/disable preference
  onQuizDialogEnabledChange?: (enabled: boolean) => void; // Callback to update preference
  roleRangerEnabled?: boolean; // Role Ranger enable/disable preference
  onRoleRangerEnabledChange?: (enabled: boolean) => void; // Callback to update preference
  pronunciationWsClientRef?: React.MutableRefObject<PronunciationWebSocketClient | null>; // Pronunciation WebSocket client ref from App
}

export default function StoryPane({ 
  ageProfile, 
  userAge, 
  userGrade,
  conversationId,
  isRecording,
  onStorySelect, 
  onReadingModeChange,
  voiceLiveClient,
  quizDialogEnabled = false,
  onQuizDialogEnabledChange,
  roleRangerEnabled = true,
  onRoleRangerEnabledChange,
  pronunciationWsClientRef
}: StoryPaneProps) {
  const [stories, setStories] = useState<Story[]>([]);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReadingMode, setIsReadingMode] = useState(false);
  
  // Pronunciation assessment state - ENABLED BY DEFAULT for automatic PronunciationCoach persona testing
  const [pronunciationEnabled, setPronunciationEnabled] = useState(true);
  const [pronunciationActive, setPronunciationActive] = useState(false);
  // const [_pronunciationError, setPronunciationError] = useState<string | null>(null);
  const [pronunciationPanelExpanded, setPronunciationPanelExpanded] = useState(false);
  
  // Pronunciation WebSocket client ref comes from App.tsx prop
  // Store hints by word_sequence_number (position in story) instead of word text to handle duplicate words
  const [realtimeHints, setRealtimeHints] = useState<Map<number, {word: string, accuracy: number, ipa: string, timestamp: number}>>(new Map());
  
  // Real-time word highlighting as user speaks
  const [currentWordIndex, setCurrentWordIndex] = useState<number>(-1);
  const previousWordIndexRef = useRef<number>(-1);

  // Reading progress tracking state
  const [readingProgress, setReadingProgress] = useState<ReadingProgressUpdate | null>(null);
  
  // Story analysis state
  const [storyAnalysis, setStoryAnalysis] = useState<StoryAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // const [_analysisError, setAnalysisError] = useState<string | null>(null);
  
  // Summary generation state
  const [summaryExists, setSummaryExists] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [summaryStorySessionId, setSummaryStorySessionId] = useState<string | null>(null);
  
  // Ref for auto-scrolling to current word
  const storyContentRef = useRef<HTMLDivElement>(null);
  const currentWordRef = useRef<HTMLSpanElement>(null);
  
  // Story progress bar state
  const [storyContentHeight, setStoryContentHeight] = useState<number>(600);
  
  // Active persona state for progress bar hint and animations
  const [activePersona, setActivePersona] = useState<string | null>(null);
  
  // Mic button hint state (show only once when user first enters reading mode)
  const [showMicHint, setShowMicHint] = useState(false);
  const [micHintDismissed, setMicHintDismissed] = useState(false);

  // Memoize the onSegmentAssessed callback to prevent unnecessary re-renders
  const handleSegmentAssessed = useCallback((segment: number, accuracy: number) => {
    logger.logUIEvent('pronunciation_segment_assessed', {
      segment,
      accuracy,
      conversation_id: conversationId
    });
  }, [conversationId]);

  // Initialize pronunciation WebSocket client when pronunciation is enabled
  useEffect(() => {
    if (!pronunciationEnabled || !conversationId) {
      // Cleanup existing client if pronunciation disabled
      if (pronunciationWsClientRef?.current) {
        pronunciationWsClientRef.current.disconnect();
        pronunciationWsClientRef.current = null;
      }
      return;
    }

    // Don't create new client if one already exists and is connected
    if (pronunciationWsClientRef?.current && pronunciationWsClientRef.current.isConnected()) {
      return;
    }

    // Create pronunciation WebSocket client
    const pronunciationWsClient = new PronunciationWebSocketClient(conversationId);

    // Handle connection established
    pronunciationWsClient.on('connected', () => {
      console.log('🎙️ Pronunciation WebSocket connected and ready');
      // Set story if available
      if (selectedStory?.content) {
        pronunciationWsClient.setStory(selectedStory.content);
        console.log('📖 Story set for pronunciation assessment');
      }
    });

    // Handle current word update for real-time highlighting
    pronunciationWsClient.on('current_word_update', (data) => {
      const newIndex = data.current_word_index;
      console.log(`[Highlighting] Word index: ${newIndex}, partial text: "${data.partial_text}"`);
      
      // Remove highlight from previous word
      if (previousWordIndexRef.current >= 0) {
        const prevElement = document.getElementById(`word-${previousWordIndexRef.current}`);
        if (prevElement) {
          prevElement.classList.remove('active-word');
          console.log(`[Highlighting] Removed highlight from word-${previousWordIndexRef.current}`);
        }
      }
      
      // Add highlight to current word (only if it exists and hasn't been highlighted yet)
      if (newIndex >= 0) {
        const currentElement = document.getElementById(`word-${newIndex}`);
        if (currentElement) {
          currentElement.classList.add('active-word');
          console.log(`[Highlighting] Added highlight to word-${newIndex}: "${currentElement.textContent}"`);
          
          // Scroll into view smoothly
          currentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        } else {
          console.warn(`[Highlighting] Element word-${newIndex} not found in DOM`);
        }
      }
      
      // Update refs and state
      previousWordIndexRef.current = newIndex;
      setCurrentWordIndex(newIndex);
    });

    // Handle real-time pronunciation hints
    pronunciationWsClient.on('realtime_hint', (data) => {
      console.log('📊 Real-time pronunciation hint:', data);
      
      // Store hint by word sequence number (position in story) to handle duplicate words correctly
      setRealtimeHints(prev => {
        const updated = new Map(prev);
        updated.set(data.word_sequence_number, {
          word: data.word,
          accuracy: data.accuracy,
          ipa: data.ipa,
          timestamp: Date.now()
        });
        console.log(`✅ [Hints] Added "${data.word}" at position ${data.word_sequence_number} (${data.accuracy}%). Total hints: ${updated.size}`);        
        return updated;
      });
    });

    // Handle final assessment
    pronunciationWsClient.on('assessment_complete', (result) => {
      console.log('✅ Pronunciation assessment complete:', result);
      setPronunciationActive(false);
      
      // Remove highlight from last word when assessment completes
      if (previousWordIndexRef.current >= 0) {
        const lastElement = document.getElementById(`word-${previousWordIndexRef.current}`);
        if (lastElement) {
          lastElement.classList.remove('active-word');
          console.log(`[Highlighting] Removed highlight from word-${previousWordIndexRef.current} (assessment complete)`);
        }
      }
      
      // DON'T reset pronunciationCurrentWordIndex here - preserve it so next session can continue from this position
      
      logger.logUIEvent('pronunciation_assessment_complete', {
        accuracy: result.accuracy,
        fluency: result.fluency,
        prosody: result.prosody,
        word_count: result.word_count
      }, userAge, conversationId);
    });

    // Handle errors
    pronunciationWsClient.on('error', (error) => {
      console.error('❌ Pronunciation WebSocket error:', error);
      // setPronunciationError(error.message);
      setPronunciationActive(false);
    });

    // Store reference in parent ref from App.tsx
    if (pronunciationWsClientRef) {
      pronunciationWsClientRef.current = pronunciationWsClient;
    }

    // Connect to WebSocket
    pronunciationWsClient.connect();

    // Cleanup on unmount or when pronunciation disabled
    return () => {
      if (pronunciationWsClient.isConnected()) {
        pronunciationWsClient.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronunciationEnabled, conversationId, userAge, pronunciationWsClientRef]);

  // Separate effect to update story when it changes (without reconnecting WebSocket)
  useEffect(() => {
    if (pronunciationEnabled && pronunciationWsClientRef?.current && 
        pronunciationWsClientRef.current.isConnected() && selectedStory?.content) {
      pronunciationWsClientRef.current.setStory(selectedStory.content);
      console.log('📖 Story updated for pronunciation assessment');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStory?.content, pronunciationEnabled]);

  // Cleanup highlighting when story changes or component unmounts
  useEffect(() => {
    return () => {
      // Remove any active highlighting when component unmounts or story changes
      if (previousWordIndexRef.current >= 0) {
        const prevElement = document.getElementById(`word-${previousWordIndexRef.current}`);
        if (prevElement) {
          prevElement.classList.remove('active-word');
        }
      }
      previousWordIndexRef.current = -1;
      setCurrentWordIndex(-1);
    };
  }, [selectedStory?.id]);

  // Reapply highlighting after DOM re-renders (e.g., when persona changes or state updates)
  // Only reapply if we have a valid word index that was set by speech (not initial -1 or 0)
  useEffect(() => {
    if (currentWordIndex > 0 && previousWordIndexRef.current === currentWordIndex) {
      // Use setTimeout to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const currentElement = document.getElementById(`word-${currentWordIndex}`);
        if (currentElement && !currentElement.classList.contains('active-word')) {
          currentElement.classList.add('active-word');
          console.log(`[Highlighting] Reapplied highlight to word-${currentWordIndex} after re-render`);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentWordIndex]);

  // Fetch stories from database based on grade (preferred) or age (fallback)
  useEffect(() => {
    const fetchStories = async () => {
      try {
        setLoading(true);
        
        // Use grade if available, otherwise fallback to age
        const queryParam = userGrade 
          ? `grade=${encodeURIComponent(userGrade)}` 
          : `age=${userAge}`;
        
        const response = await fetch(getApiUrl(`/v1/dashboard/student-stories?${queryParam}`));
        
        if (!response.ok) {
          throw new Error('Failed to fetch stories from database');
        }
        
        const data = await response.json();
        setStories(data.stories || []);
        setError(null);
        
        logger.logUIEvent('stories_fetched_from_database', {
          count: data.stories?.length || 0,
          source: data.source,
          filter_type: data.filter_type || (userGrade ? 'grade' : 'age'),
          filter_value: data.filter_value || (userGrade || userAge),
          filtered_by_grade: data.filtered_by_grade,
          filtered_by_age: data.filtered_by_age,
          user_age: userAge,
          user_grade: userGrade
        }, userAge);

        console.log(`📚 Stories loaded with ${data.filter_type || (userGrade ? 'grade' : 'age')} filtering:`, {
          filter_value: userGrade || `age ${userAge}`,
          stories_count: data.stories?.length || 0,
          grade_levels_found: Array.from(new Set(data.stories?.map((s: any) => s.grade_level) || []))
        });
        
      } catch (err) {
        console.error('Error fetching stories from database:', err);
        setError('Failed to load stories from database. Please try again.');
        
        logger.logErrorEvent('stories_fetch_database_failed', err as Error, {
          user_age: userAge,
          user_grade: userGrade
        }, userAge);
      } finally {
        setLoading(false);
      }
    };

    if (userAge || userGrade) {
      fetchStories();
    }
  }, [userAge, userGrade]);

  // Auto-start/stop pronunciation assessment based on mic state and toggle
  useEffect(() => {
    const handlePronunciationAssessment = async () => {
      const pronunciationWsClient = pronunciationWsClientRef?.current;
      
      if (!pronunciationWsClient) return;

      // Start conditions: Toggle ON + Mic Recording + Story Selected + Reading Mode + Not Already Active
      const shouldStart = pronunciationEnabled && isRecording && selectedStory && isReadingMode && !pronunciationActive;
      
      // Stop conditions: Mic Stopped OR Toggle OFF OR No Story
      const shouldStop = pronunciationActive && (!isRecording || !pronunciationEnabled || !selectedStory);

      if (shouldStart) {
        try {
          logger.logUIEvent('pronunciation_auto_start', {
            story_id: selectedStory?.id,
            story_title: selectedStory?.title,
            session_id: conversationId
          }, userAge, conversationId);

          // Start assessment from beginning (no position tracking)
          const startPosition = 0;
          console.log(`📍 Starting pronunciation assessment from beginning`);

          // Start assessment via WebSocket
          pronunciationWsClient.startAssessment(startPosition);
          setPronunciationActive(true);
          // setPronunciationError(null);

          logger.logUIEvent('pronunciation_started', {
            story_id: selectedStory?.id,
            mode: 'websocket',
            session_id: conversationId
          }, userAge, conversationId);

        } catch (error) {
          console.error('Failed to start pronunciation assessment:', error);
          // setPronunciationError('Failed to start pronunciation assessment');
          logger.logErrorEvent('pronunciation_start_failed', error as Error, {
            session_id: conversationId
          }, userAge, conversationId);
        }
      }

      if (shouldStop) {
        try {
          logger.logUIEvent('pronunciation_auto_stop', {
            story_id: selectedStory?.id,
            session_id: conversationId,
            reason: !isRecording ? 'mic_stopped' : !pronunciationEnabled ? 'toggle_off' : 'story_cleared'
          }, userAge, conversationId);

          // Stop assessment via WebSocket (triggers final assessment)
          pronunciationWsClient.endAssessment();
          setPronunciationActive(false);
          // setPronunciationError(null);

          logger.logUIEvent('pronunciation_stopped', {
            session_id: conversationId
          }, userAge, conversationId);

        } catch (error) {
          console.error('Failed to stop pronunciation assessment:', error);
          setPronunciationActive(false);
          // setPronunciationError('Failed to complete pronunciation assessment');
          logger.logErrorEvent('pronunciation_stop_failed', error as Error, {
            session_id: conversationId
          }, userAge, conversationId);
        }
      }
    };

    handlePronunciationAssessment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronunciationEnabled, isRecording, selectedStory, isReadingMode, pronunciationActive, conversationId, userAge]);

  // Show mic hint when entering reading mode for the first time
  useEffect(() => {
    if (isReadingMode && selectedStory && !micHintDismissed && !showMicHint) {
      // Show hint after a short delay
      const timer = setTimeout(() => {
        setShowMicHint(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isReadingMode, selectedStory, micHintDismissed, showMicHint]);
  
  // Auto-dismiss mic hint after 10 seconds
  useEffect(() => {
    if (showMicHint) {
      const timer = setTimeout(() => {
        setShowMicHint(false);
        setMicHintDismissed(true);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showMicHint]);

  // Subscribe to reading progress updates from WebSocket
  useEffect(() => {
    if (!voiceLiveClient || !conversationId) return;

    // Subscribe to reading progress events from VoiceLive client
    const handleReadingProgress = (progressData: any) => {
      console.log('📖 Reading progress received in StoryPane:', progressData);
      setReadingProgress(progressData);
    };

    // Listen for reading progress events
    voiceLiveClient.addEventListener('readingProgress', handleReadingProgress);

    // Also subscribe to reading progress service
    const unsubscribe = readingProgressService.subscribe((update) => {
      if (update && update.conversation_id === conversationId) {
        setReadingProgress(update);
      }
    });

    // Cleanup
    return () => {
      voiceLiveClient.removeEventListener('readingProgress', handleReadingProgress);
      unsubscribe();
    };
  }, [voiceLiveClient, conversationId]);

  // Auto-scroll to current word when progress updates
  useEffect(() => {
    if (currentWordRef.current && storyContentRef.current) {
      const currentWordElement = currentWordRef.current;
          // const container = storyContentRef.current;      // Scroll the current word into view smoothly
      currentWordElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, [readingProgress?.progress?.current_word_index]);

  // Handle pronunciation toggle
  const handlePronunciationToggle = useCallback(() => {
    const newState = !pronunciationEnabled;
    setPronunciationEnabled(newState);
    // setPronunciationError(null);

    logger.logUIEvent('pronunciation_toggle', {
      enabled: newState,
      story_id: selectedStory?.id,
      session_id: conversationId
    }, userAge, conversationId);
  }, [pronunciationEnabled, selectedStory, conversationId, userAge]);

  // Measure story content height for progress bar
  useEffect(() => {
    if (!storyContentRef.current) return;

    const updateHeight = () => {
      if (storyContentRef.current) {
        const height = storyContentRef.current.scrollHeight;
        setStoryContentHeight(height);
      }
    };

    // Initial measurement
    updateHeight();

    // Update on window resize
    window.addEventListener('resize', updateHeight);

    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(storyContentRef.current);

    return () => {
      window.removeEventListener('resize', updateHeight);
      resizeObserver.disconnect();
    };
  }, [selectedStory, readingProgress]);

  const handleStorySelect = async (story: Story) => {
    setSelectedStory(story);
    onStorySelect(story);
    
    // ========================================================================
    // CLEANUP PREVIOUS STORY STATE
    // ========================================================================
    // Clear reading progress from previous story
    setReadingProgress(null);
    readingProgressService.clearProgress();
    
    // Clear summary state from previous story
    setSummaryExists(false);
    setIsGeneratingSummary(false);
    setSummaryError(null);
    setShowSummaryModal(false);
    setShowSummaryPanel(false);
    setSummaryStorySessionId(null);
    
    // Clear story analysis from previous story
    setStoryAnalysis(null);
    // setAnalysisError(null);
    
    // Clear pronunciation state from previous story
    setPronunciationActive(false);
    // setPronunciationError(null);
    
    logger.logUIEvent('previous_story_state_cleared', {
      new_story_id: story.id,
      new_story_title: story.title,
      conversation_id: conversationId
    }, userAge, conversationId);
    // ========================================================================
    
    // Update reading state
    const newReadingState: StoryReadingState = {
      isReadingMode: true,
      currentStory: story,
      readingProgress: 0
    };
    
    setIsReadingMode(true);
    onReadingModeChange(newReadingState);
    
    // Trigger story analysis for persona readiness
    setIsAnalyzing(true);
    // setAnalysisError(null);
    
    try {
      const analysis = await storyAnalysisService.analyzeStory(
        story.id.toString(),
        false, // Don't force refresh - use cache if available
        userAge,
        conversationId
      );
      
      if (analysis) {
        setStoryAnalysis(analysis);
        
        logger.logUIEvent('story_analysis_complete', {
          story_id: story.id,
          story_title: story.title,
          quiz_ready: analysis.persona_readiness.quiz_critter.ready,
          quiz_count: analysis.persona_readiness.quiz_critter.opportunities_count,
          roleplay_ready: analysis.persona_readiness.role_ranger.ready,
          roleplay_count: analysis.persona_readiness.role_ranger.opportunities_count,
          cached: analysis.analysis_cached
        }, userAge, conversationId);
      } else {
        // setAnalysisError('Failed to analyze story');
      }
    } catch (error) {
      console.error('Story analysis error:', error);
      // setAnalysisError('Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
    
    // Story announcement and reading progress tracking is handled by storyContextService in App.tsx
    // No need to call the announcement API here to avoid duplicate announcements
  };

  const handleClearStory = async () => {
    // Check if story is completed but summary not generated
    const completionPercentage = readingProgress?.progress?.completion_percentage || 0;
    
    if (completionPercentage >= 100 && !summaryExists && readingProgress?.progress?.story_session_id) {
      const shouldGenerate = window.confirm(
        "You've completed this story but haven't generated a summary yet.\n\n" +
        "Would you like to generate your reading summary before switching stories?\n\n" +
        "Click OK to generate summary, or Cancel to switch anyway."
      );
      
      if (shouldGenerate) {
        // Trigger summary generation instead of clearing
        handleGenerateSummary();
        return; // Don't clear the story yet
      }
    }
    
    setSelectedStory(null);
    setIsReadingMode(false);
    setStoryAnalysis(null);
    // setAnalysisError(null);
    setSummaryExists(false);
    onStorySelect(null);
    
    const newReadingState: StoryReadingState = {
      isReadingMode: false,
      currentStory: null,
      readingProgress: 0
    };
    
    onReadingModeChange(newReadingState);
    
    // Clear reading progress session
    try {
      await fetch(getApiUrl(`/voicelive/reading/session/${conversationId}`), {
        method: 'DELETE'
      });
      console.log('✅ Reading progress session cleared');
    } catch (error) {
      console.error('Error clearing reading progress session:', error);
    }
  };

  // Handle Generate Summary button click
  const handleGenerateSummary = async () => {
    if (!selectedStory || !readingProgress?.progress?.story_session_id) {
      setSummaryError('No active reading session found');
      return;
    }

    const completionPercentage = readingProgress.progress.completion_percentage || 0;
    if (completionPercentage < 100) {
      setSummaryError('Please complete the story to 100% before generating summary');
      return;
    }

    setIsGeneratingSummary(true);
    setSummaryError(null);

    try {
      // Start summary generation
      await summaryService.generateSummary(readingProgress.progress.story_session_id);
      
      logger.logUIEvent('summary_generation_started', {
        story_session_id: readingProgress.progress.story_session_id,
        story_id: selectedStory.id,
        story_title: selectedStory.title,
        conversation_id: conversationId
      }, userAge);

      // Show modal with polling
      setSummaryStorySessionId(readingProgress.progress.story_session_id);
      setShowSummaryModal(true);
      
    } catch (error: any) {
      console.error('Error starting summary generation:', error);
      setSummaryError(error.message || 'Failed to start summary generation');
      setIsGeneratingSummary(false);
      
      logger.logUIEvent('summary_generation_start_failed', {
        story_session_id: readingProgress.progress.story_session_id,
        error: error.message
      }, userAge);
    }
  };

  const handleSummaryComplete = () => {
    setShowSummaryModal(false);
    setIsGeneratingSummary(false);
    setSummaryExists(true);
    
    logger.logUIEvent('summary_generation_completed', {
      story_session_id: summaryStorySessionId
    }, userAge);

    // Show the summary panel
    setShowSummaryPanel(true);
  };

  const handleSummaryError = (error: string) => {
    setShowSummaryModal(false);
    setIsGeneratingSummary(false);
    setSummaryError(error);
    
    logger.logUIEvent('summary_generation_failed', {
      story_session_id: summaryStorySessionId,
      error
    }, userAge);
  };

  const handleSummaryModalClose = () => {
    setShowSummaryModal(false);
    setIsGeneratingSummary(false);
    setSummaryError('Summary generation cancelled');
  };

  const handleViewSummary = () => {
    if (summaryStorySessionId) {
      setShowSummaryPanel(true);
    }
  };

  const formatContent = (content: string) => {
    // If no reading progress, render plain text with paragraph structure
    // BUT still apply pronunciation hints if available (from realtime assessment)
    if (!readingProgress || !readingProgress.progress) {
      // Split into paragraphs
      const paragraphs = content.split('\n\n');
      let wordCounter = 0;
      
      return (
        <div className={`${ageProfile.theme.typography.bodySize} leading-relaxed`}>
          {paragraphs.map((paragraph, pIndex) => {
            const lines = paragraph.split('\n');
            
            return (
              <p key={pIndex} className="mb-4">
                {lines.map((line, lineIndex) => {
                  const words = line.split(/(\s+)/);
                  const highlightedWords: JSX.Element[] = [];
                  
                  words.forEach((segment, wIndex) => {
                    const isWord = segment.trim().length > 0;
                    
                    if (isWord) {
                      // Use word sequence number (wordCounter) to get hint for THIS specific word occurrence
                      const pronunciationHint = realtimeHints.get(wordCounter);
                      
                      let pronunciationClass = 'word';
                      let pronunciationTitle = '';
                      if (pronunciationHint) {
                        const accuracy = pronunciationHint.accuracy;
                        if (accuracy >= 80) {
                          pronunciationClass = 'word';
                          pronunciationTitle = `Excellent! ${Math.round(accuracy)}% accuracy\nIPA: ${pronunciationHint.ipa}`;
                        } else if (accuracy >= 60) {
                          pronunciationClass = 'word';
                          pronunciationTitle = `Good try! ${Math.round(accuracy)}% accuracy\nIPA: ${pronunciationHint.ipa}`;
                        } else {
                          pronunciationClass = 'word word-bad';
                          pronunciationTitle = `Needs practice: ${Math.round(accuracy)}% accuracy\nIPA: ${pronunciationHint.ipa}`;
                        }
                      }
                      
                      highlightedWords.push(
                        <span
                          key={`${pIndex}-${lineIndex}-${wIndex}`}
                          id={`word-${wordCounter}`}
                          className={pronunciationClass}
                          title={pronunciationTitle}
                        >
                          {segment}
                        </span>
                      );
                      wordCounter++;
                    } else {
                      highlightedWords.push(
                        <span key={`${pIndex}-${lineIndex}-${wIndex}-space`}>{segment}</span>
                      );
                    }
                  });
                  
                  return (
                    <React.Fragment key={lineIndex}>
                      {highlightedWords}
                      {lineIndex < lines.length - 1 && <br />}
                    </React.Fragment>
                  );
                })}
              </p>
            );
          })}
        </div>
      );
    }

    // Split content into paragraphs first to preserve structure
    const paragraphs = content.split('\n\n');
    
    let wordCounter = 0;
    
    return (
      <div className={`${ageProfile.theme.typography.bodySize} leading-relaxed`}>
        {paragraphs.map((paragraph, pIndex) => {
          // Handle single line breaks within paragraphs
          const lines = paragraph.split('\n');
          
          return (
            <p key={pIndex} className="mb-4">
              {lines.map((line, lineIndex) => {
                // Split line into words while preserving whitespace
                const words = line.split(/(\s+)/);
                const highlightedWords: JSX.Element[] = [];
                
                words.forEach((segment, wIndex) => {
                  // Check if this is a word (not whitespace)
                  const isWord = segment.trim().length > 0;
                  
                  if (isWord) {
                    // Check if this word has a pronunciation hint from realtime assessment
                    // Use word sequence number (wordCounter) as key to handle duplicate words correctly
                    // Only words that have actually been spoken will have hints
                    const pronunciationHint = realtimeHints.get(wordCounter);
                    
                    // Store current word index for this iteration
                    const currentWordSequence = wordCounter;
                    
                    // Determine pronunciation feedback based on accuracy
                    let pronunciationClass = 'word';
                    let pronunciationTitle = '';
                    if (pronunciationHint) {
                      const accuracy = pronunciationHint.accuracy;
                      if (accuracy >= 80) {
                        pronunciationClass = 'word';
                        pronunciationTitle = `Excellent! ${Math.round(accuracy)}% accuracy\nIPA: ${pronunciationHint.ipa}`;
                      } else if (accuracy >= 60) {
                        pronunciationClass = 'word';
                        pronunciationTitle = `Good try! ${Math.round(accuracy)}% accuracy\nIPA: ${pronunciationHint.ipa}`;
                      } else {
                        pronunciationClass = 'word word-bad';
                        pronunciationTitle = `Needs practice: ${Math.round(accuracy)}% accuracy\nIPA: ${pronunciationHint.ipa}`;
                      }
                    }
                    
                    highlightedWords.push(
                      <span
                        key={`${pIndex}-${lineIndex}-${wIndex}`}
                        id={`word-${currentWordSequence}`}
                        className={`
                          ${pronunciationClass}
                          cursor-pointer hover:opacity-80
                        `}
                        title={pronunciationTitle}
                      >
                        {segment}
                      </span>
                    );
                    
                    wordCounter++;
                  } else {
                    // Whitespace - render as-is
                    highlightedWords.push(
                      <span key={`${pIndex}-${lineIndex}-${wIndex}`}>{segment}</span>
                    );
                  }
                });
                
                // Add line break between lines (except for the last line)
                return (
                  <span key={`${pIndex}-line-${lineIndex}`}>
                    {highlightedWords}
                    {lineIndex < lines.length - 1 && <br />}
                  </span>
                );
              })}
            </p>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" 
                 style={{ borderColor: ageProfile.theme.colors.primary }}></div>
            <p className="text-gray-600">Loading stories...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <ErrorCircleIcon className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-600 mb-2">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="text-sm text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-w-full overflow-hidden">
      {/* Header with Clear Story Button and Persona Indicator */}
      {selectedStory && (
        <div className="flex flex-col gap-3 mb-4">
          {/* Top Row: Clear Story Button */}
          <div className="flex justify-end items-start gap-3">
            <button
              onClick={handleClearStory}
              className="text-sm px-3 py-1 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
            >
              Choose Different Story
            </button>
          </div>

          {/* Bottom Row: Active Persona Indicator with Readiness Badges */}
          {conversationId && isReadingMode && (
            <div className="w-full">
              <PersonaIndicatorCompact 
                conversationId={conversationId}
                storyId={selectedStory?.id || null}
                quizReadiness={storyAnalysis?.persona_readiness.quiz_critter || null}
                roleReadiness={storyAnalysis?.persona_readiness.role_ranger || null}
                isAnalyzing={isAnalyzing}
                voiceLiveClient={voiceLiveClient}
                quizDialogEnabled={quizDialogEnabled}
                onQuizDialogEnabledChange={onQuizDialogEnabledChange}
                roleRangerEnabled={roleRangerEnabled}
                onRoleRangerEnabledChange={onRoleRangerEnabledChange}
                pronunciationEnabled={pronunciationEnabled}
                onPronunciationEnabledChange={handlePronunciationToggle}
                pronunciationActive={pronunciationActive}
                onActivePersonaChange={setActivePersona}
              />
            </div>
          )}
        </div>
      )}

      {/* Floating Pronunciation Progress Panel */}
      <PronunciationProgressFloating 
        sessionId={conversationId}
        isActive={pronunciationActive}
        isExpanded={pronunciationPanelExpanded}
        onToggle={() => setPronunciationPanelExpanded(!pronunciationPanelExpanded)}
        onSegmentAssessed={handleSegmentAssessed}
        voiceLiveClient={voiceLiveClient}
      />

      {/* Story Selection or Reading Area */}
      {!selectedStory ? (
        <div className="flex-1 overflow-y-auto m-2 md:m-4">
          {stories.length === 0 ? (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: ageProfile.theme.colors.secondary }}
              >
                <BookIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className={`${ageProfile.theme.typography.headingSize} font-medium text-gray-700 mb-2`}>
                No Stories Available
              </h3>
              <p className="text-gray-500">
                {userGrade 
                  ? `Stories for ${userGrade} will appear here when they're added.`
                  : `Stories for your age group will appear here when they're added.`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className={`${ageProfile.theme.typography.bodySize} text-gray-600 mb-4`}>
                Choose a story to read:
              </p>
              
              {stories.map((story) => (
                <div
                  key={story.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-white max-w-full mr-2 md:mr-8"
                  onClick={() => handleStorySelect(story)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className={`${ageProfile.theme.typography.headingSize} font-semibold text-gray-800`}>
                      {story.title}
                    </h3>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">
                      {story.estimated_read_time} min
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(story as any).grade_level && (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 font-bold border border-green-300">
                        🎓 {(story as any).grade_level}
                      </span>
                    )}
                    {story.lexile_level && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">
                        📚 Lexile: {story.lexile_level}
                      </span>
                    )}
                    {story.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-1 rounded-full text-white"
                        style={{ backgroundColor: ageProfile.theme.colors.accent }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {story.content.substring(0, 120)}...
                  </p>
                  
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-gray-500 capitalize">
                      {story.reading_level} level
                    </span>
                    <button
                      className="text-sm font-medium px-3 py-1 rounded-full transition-colors"
                      style={{ 
                        backgroundColor: ageProfile.theme.colors.primary,
                        color: 'white'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStorySelect(story);
                      }}
                    >
                      Read Story
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Story Reading Area with Progress Bar */
        <div className="flex-1 flex flex-row overflow-hidden">
          {/* Story Progress Bar - Only show when reading mode is active and analysis complete */}
          {isReadingMode && storyAnalysis && !isAnalyzing && (
            <StoryProgressBar 
              roleplayOpportunities={storyAnalysis.roleplay_opportunities}
              quizOpportunities={storyAnalysis.quiz_opportunities}
              currentReadingPosition={readingProgress?.progress?.completion_percentage || 0}
              storyContentHeight={storyContentHeight}
              activePersona={activePersona}
            />
          )}
          
          <div className="flex-1 overflow-y-auto bg-white rounded-lg p-6 border max-w-full mr-2 md:mr-12" ref={storyContentRef}>
            {/* Mic Button Hint - Show on first reading */}
            {showMicHint && (
              <div className="mic-hint-container">
                <div className="mic-hint-callout">
                  <button 
                    onClick={() => {
                      setShowMicHint(false);
                      setMicHintDismissed(true);
                    }}
                    className="mic-hint-close"
                  >
                    ✕
                  </button>
                  <div className="mic-hint-title">🎤 Ready to Read?</div>
                  <div className="mic-hint-text">
                    Click the <strong>Microphone button</strong> on the right side to start reading aloud!
                  </div>
                </div>
                <div className="mic-hint-arrow">
                  <svg width="60" height="60" viewBox="0 0 60 60" className="animated-arrow">
                    <path 
                      d="M 10 30 L 40 30 L 35 25 M 40 30 L 35 35" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      fill="none" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            )}
            
            <div className="mb-6 max-w-full">
              <h1 className={`${ageProfile.theme.typography.headingSize} font-bold text-gray-900 mb-2 break-words`}>
                {selectedStory.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                <span>📖 {selectedStory.estimated_read_time} min read</span>
                <span className="capitalize">📚 {selectedStory.reading_level}</span>
              {selectedStory.lexile_level && (
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                  Lexile: {selectedStory.lexile_level}
                </span>
              )}
              {isReadingMode && (
                <span className="text-green-600 font-medium">
                  📢 Reading Mode Active
                </span>
              )}
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none">
            {formatContent(selectedStory.content)}
          </div>
          
          {/* Generate Summary Button*/}
          {readingProgress && readingProgress.progress && readingProgress.progress.completion_percentage >= 100 && !summaryExists && (
            <div className="mt-6">
              <button
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary}
                className={`
                  w-full px-6 py-3 rounded-lg font-semibold text-white
                  transition-all duration-200 shadow-md
                  flex items-center justify-center gap-2
                  ${isGeneratingSummary 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 hover:shadow-lg'
                  }
                `}
                title="Generate a personalized reading session summary"
              >
                {isGeneratingSummary ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Generating Summary...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                    </svg>
                    <span>✨ Generate Reading Summary</span>
                  </>
                )}
              </button>
              
              {summaryError && (
                <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  <span>{summaryError}</span>
                </div>
              )}
            </div>
          )}

          {/* Summary Already Generated Message */}
          {summaryExists && (
            <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-green-800">Summary Generated!</p>
                  <p className="text-sm text-green-700">Your reading summary is ready to view.</p>
                </div>
              </div>
              <button
                onClick={handleViewSummary}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-md font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                <span>View Your Summary</span>
              </button>
            </div>
          )}

          </div>
          
        </div>
      )}
{/* Reading Progress Display - Moved below Summary Generated message */}
          {readingProgress && readingProgress.progress && (
            <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800 text-sm">Reading Progress</span>
                </div>
                <span className="text-xs text-blue-700 font-bold">
                  {readingProgress.progress.completion_percentage.toFixed(1)}%
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-blue-100 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${readingProgress.progress.completion_percentage}%` }}
                ></div>
              </div>
              
              <div className="text-xs text-blue-700 mb-2">
                {Array.isArray(readingProgress.progress.words_read) 
                  ? readingProgress.progress.words_read.length 
                  : readingProgress.progress.words_read} / {readingProgress.progress.total_words} words
              </div>

              {/* Words Read Preview */}
              {Array.isArray(readingProgress.progress.words_read) && readingProgress.progress.words_read.length > 0 && (
                <div className="text-xs text-blue-600 mb-2 italic">
                  "{readingProgress.progress.words_read.join(' ')}..."
                </div>
              )}

              {/* Next Word to Read */}
              {readingProgress.next_word && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <div className="text-xs text-blue-600 font-semibold mb-1">Next Word:</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-blue-900">
                      {readingProgress.next_word.next_word}
                    </span>
                    <span className="text-sm text-blue-700 italic">
                      ({readingProgress.next_word.syllables})
                    </span>
                  </div>
                  {readingProgress.next_word.context_before && readingProgress.next_word.context_after && (
                    <div className="text-xs text-blue-600 mt-1">
                      ...{readingProgress.next_word.context_before} <span className="font-bold">[{readingProgress.next_word.next_word}]</span> {readingProgress.next_word.context_after}...
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
      {/* Summary Generation Modal */}
      {/* Summary Generation Modal */}
      {showSummaryModal && summaryStorySessionId && (
        <SummaryGenerationModal
          storySessionId={summaryStorySessionId}
          isOpen={showSummaryModal}
          onComplete={handleSummaryComplete}
          onError={handleSummaryError}
          onClose={handleSummaryModalClose}
        />
      )}

      {/* Reading Summary Panel */}
      {showSummaryPanel && summaryStorySessionId && (
        <ReadingSummaryPanel
          storySessionId={summaryStorySessionId}
          userAge={userAge}
          isOpen={showSummaryPanel}
          onClose={() => setShowSummaryPanel(false)}
        />
      )}
    </div>
  );
}